import * as React from 'react';
import { ReactElement } from 'react';
import { AppLink } from 'mydish-ui-util';
import '../css/key-art-poster.sass';
import expandArrow from '../css/assets/img/day-ghost-block-arrow-inactive.svg';
import ExpandedPoster from './ExpandedPoster';
import StaticElementButton from './StaticElementButton';

interface KeyArtPosterProps {
  imageUrl: string;
  id: string;
  title: string;
  expandable?: boolean;
  bgImage?: string;
  theme?: 'night' | 'day';
  children?: ReactElement | ReactElement[] | string | (string | ReactElement)[];
  onExpand?: Function;
  onClose?: Function;
  focusable?: boolean;
  centeredCarousel?: boolean;
  /**
   * refElemId is the id of the parent element enclosing row of multiple KeyArtPosters. If using refElemId, it is required to
   * pass id to the parent and it should be same as refElemId. It is to be used in case where we need to show expanded poster.
   */
  refElemId?: string;
  nextAiringOn?: React.ReactNode;
  eventDataPoster?: React.ReactNode;
  showEventDetails?: boolean;
}

export default function KeyArtPoster(props: KeyArtPosterProps) {
  const { theme = 'day', expandable = true } = props;

  const findParent = (elm: any): any => {
    if (props.refElemId) {
      return document.getElementById(props.refElemId);
    }
    if (!elm) return null;
    // @ts-ignore
    if (
      elm &&
      elm.parentNode &&
      elm.parentNode.getAttribute &&
      elm.parentNode.getAttribute('id')
    )
      return elm.parentNode;
    return findParent(elm.parentNode);
  };

  const changeTabValueForStaticElementButtons = (tab: string) => {
    const keyArtContainer = findParent(document.getElementById(props.id));
    // eslint-disable-next-line no-unused-expressions
    keyArtContainer
      .querySelectorAll('.staticElementButton')
      ?.forEach((elm: any) => {
        elm.setAttribute('tabIndex', tab);
      });
  };

  const hideDetails = () => {
    const keyArtContainer: any = findParent(document.getElementById(props.id));
    if (keyArtContainer) {
      const keyArtPosters: any =
        keyArtContainer.querySelectorAll('.key-art-poster');
      const { nextSibling } = keyArtContainer;
      if (
        nextSibling &&
        nextSibling.classList &&
        nextSibling.classList.contains('show-details')
      ) {
        keyArtContainer.parentNode.removeChild(nextSibling);
      }

      // eslint-disable-next-line no-unused-expressions
      keyArtPosters?.forEach((element: any) => {
        element.classList.remove('blur');
      });
      const sourceLink: any = document.getElementById(`${props.id}-btn`);
      sourceLink.classList.remove('disabled');
      changeTabValueForStaticElementButtons('0');
      sourceLink.tabIndex = 0;
      sourceLink.focus();
      if (props.onClose) props.onClose();
    }
  };

  const showDetails = (event: any) => {
    event.target.focus();
    const sourceElm = document.getElementById(
      `${props.id.toLowerCase()}-expanded`
    );

    if (sourceElm) {
      const copySource: any = sourceElm.cloneNode(true);
      copySource.classList.add('show-details');
      copySource.setAttribute('aria-hidden', 'false');
      copySource.removeAttribute('id');
      event.target.blur();
      const keyArtContainer = findParent(document.getElementById(props.id));
      if (keyArtContainer) {
        const { nextSibling } = keyArtContainer;
        if (
          nextSibling &&
          nextSibling.classList &&
          nextSibling.classList.contains('show-details')
        ) {
          keyArtContainer.parentNode.removeChild(nextSibling);
        }
        copySource.style.width = `${keyArtContainer.offsetWidth}px`;
        keyArtContainer.parentNode.insertBefore(
          copySource,
          keyArtContainer.nextSibling
        );
        const keyArtPosters =
          keyArtContainer.querySelectorAll('.key-art-poster');
        keyArtPosters?.forEach((element: any) => {
          if (element.getAttribute('id') === props.id) {
            element.classList.remove('blur');
          } else {
            element.classList.add('blur');
          }
        });
        const sourceLink: any = document.getElementById(`${props.id}-btn`);
        sourceLink.classList.add('disabled');
        changeTabValueForStaticElementButtons('-1');
        const closeIcon = copySource.childNodes[0];
        copySource.tabIndex = 0;
        copySource.focus();
        if (closeIcon) {
          closeIcon.tabIndex = 0;
          closeIcon.ariaLabel = 'close';
          closeIcon.removeAttribute('id');
          closeIcon.addEventListener('click', hideDetails, true);
          closeIcon.addEventListener(
            'keydown',
            (e: any) => {
              if (e.key === 'Enter') hideDetails();
            },
            true
          );
        }
        if (props.onExpand) props.onExpand();
      }
    }
  };

  const children = React.Children.toArray(props.children).filter(
    (child: any) => {
      return React.isValidElement(child) && child.type !== React.Fragment;
    }
  );

  const image = (
    <img
      alt={
        !props.centeredCarousel || (props.centeredCarousel && props.focusable)
          ? props.title
          : ''
      }
      src={props.imageUrl}
    />
  );

  let imageHref: string = '';

  if (props.children) {
    const childrenArray = Array.isArray(props.children)
      ? props.children
      : [props.children];
    childrenArray.forEach((child: ReactElement | string) => {
      if (typeof child !== 'string' && child?.props?.href) {
        imageHref = child.props.href;
      }
    });
  }

  return (
    <div
      className={`key-art-poster ${theme}`}
      id={props.id ? props.id : undefined}
      key={props.title}
      data-bgimage={props.bgImage}
      role={props.centeredCarousel ? 'figure' : ''}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={props.focusable ? 0 : -1}
    >
      {!props.nextAiringOn && (
        <div>
          {imageHref && props.focusable ? (
            <AppLink href={imageHref}>{image}</AppLink>
          ) : (
            image
          )}
        </div>
      )}
      {props.nextAiringOn && (
        <div className="img-wrapper">
          {imageHref && props.focusable ? (
            <AppLink href={imageHref}>{image}</AppLink>
          ) : (
            image
          )}
          <div className="img-details">{props.nextAiringOn}</div>
        </div>
      )}
      {expandable ? (
        <>
          {children.length > 0 ? (
            <div className="event-data-poster">
              {!!props.eventDataPoster && props.eventDataPoster}
              {(!props.eventDataPoster || props.showEventDetails) && (
                <StaticElementButton
                  type="div"
                  name="expand-btn"
                  label={`${props.title} expand-button`}
                  id={`${props.id}-btn`}
                  className="title"
                  handleKeyDown={(e: any) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      showDetails(e);
                    }
                  }}
                  onClick={showDetails}
                >
                  {!props.eventDataPoster && props.title}
                  <img
                    className="ml-2"
                    id={`expand-content-${props.id.toLowerCase()}`}
                    src={expandArrow}
                    alt="expand details"
                  />
                </StaticElementButton>
              )}
            </div>
          ) : (
            <div className="title">{props.title}</div>
          )}
          {children.length > 0 && (
            <ExpandedPoster theme={theme} id={props.id} onClose={hideDetails}>
              {props.children}
            </ExpandedPoster>
          )}
        </>
      ) : (
        <div>
          {props.children && (
            <div
              id={props.id ? `${props.id}-caption` : undefined}
              className="poster-description"
            >
              {props.children}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
